import { GlassCard } from "@/components/GlassCard";
import { CalendarDays, Check } from "lucide-react";
import { dateKey, fmtUSD } from "@/lib/format";

function buildMonthGrid(monthKeyStr) {
  const [y, m] = monthKeyStr.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(y, m, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, m - 1, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

const compactUSD = (n) => {
  if (!n) return "";
  if (n >= 1000) return `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return `$${Math.round(n)}`;
};

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

export function WorkCalendar({ monthKeyStr, workdays, onToggle, dailyMap, basePay }) {
  const cells = buildMonthGrid(monthKeyStr);
  const workedSet = new Set((workdays || []).map((w) => w.work_date));
  const today = dateKey(new Date());
  const daysWorked = workdays?.length || 0;
  const workedToday = workedSet.has(today);

  const handleClick = (k, dateObj) => {
    onToggle(k, dateObj.getDay() === 0); // pass isSunday flag
  };

  return (
    <GlassCard data-testid="work-calendar">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl glass-pill flex items-center justify-center">
            <CalendarDays className="w-5 h-5 text-white" strokeWidth={2.2} />
          </div>
          <div>
            <p className="font-heading text-base font-bold text-white">Workday Calendar</p>
            <p className="text-xs text-white/55">
              {daysWorked} days · {fmtUSD(daysWorked * basePay)} base pay
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            const d = new Date();
            onToggle(today, d.getDay() === 0);
          }}
          className={`inline-flex items-center justify-center px-3 h-8 rounded-full text-xs font-semibold border active:opacity-70 leading-none ${
            workedToday
              ? "bg-white text-black border-white"
              : "glass-pill text-white"
          }`}
          data-testid="worked-today-toggle"
        >
          {workedToday ? (
            <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Worked</span>
          ) : (
            "Working today"
          )}
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1.5 text-center">
        {WEEKDAYS.map((d, i) => (
          <div key={`wd-${i}`} className="text-[10px] uppercase tracking-wider text-white/40 py-1">{d}</div>
        ))}
        {cells.map((cell, i) => {
          if (!cell) return <div key={`empty-${i}`} />;
          const k = dateKey(cell);
          const worked = workedSet.has(k);
          const dayData = dailyMap?.[k];
          const dayEarned = dayData?.total || 0;
          const isToday = k === today;
          return (
            <button
              key={k}
              onClick={() => handleClick(k, cell)}
              className={`relative aspect-square rounded-xl active:opacity-70 flex flex-col items-center justify-center gap-0.5 px-1
                ${worked
                  ? "bg-white text-black border border-white"
                  : "bg-white/[0.04] text-white/85 border border-white/10"
                }
                ${isToday && !worked ? "ring-2 ring-white/70" : ""}
                ${isToday && worked ? "ring-2 ring-white/50" : ""}`}
              data-testid={`day-cell-${k}`}
            >
              <span className={`text-sm font-semibold leading-none ${worked ? "text-black" : "text-white"}`}>
                {cell.getDate()}
              </span>
              {dayEarned > 0 && (
                <span
                  className={`text-[11px] font-heading font-bold tabular-nums leading-none ${
                    worked ? "text-black/80" : "text-emerald-300"
                  }`}
                  data-testid={`day-earned-${k}`}
                >
                  {compactUSD(dayEarned)}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex items-center gap-4 text-[11px] text-white/50">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-white" /> Worked
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="font-mono text-emerald-300">$</span> Daily earnings
        </span>
      </div>
    </GlassCard>
  );
}
